import { useState, useEffect } from 'react';
import { createPublicClient, http, formatUnits, type Address } from 'viem';
import { base } from 'viem/chains';
import { useAuth } from './useAuth';
import { ALCHEMY_API_KEY } from '@/utils/constants';

// USDC contract address on Base Mainnet
const USDC_ADDRESS = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913' as Address;

// Minimal ERC20 ABI - just the balanceOf function
const ERC20_ABI = [
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
] as const;

export function useUSDCBalance() {
  const { privyUser } = useAuth();
  const [balance, setBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get wallet address from Privy embedded wallet
      const walletAddress = privyUser?.wallet?.address;

      if (!walletAddress) {
        setBalance(0);
        setIsLoading(false);
        return;
      }

      // Create public client for reading blockchain data
      const publicClient = createPublicClient({
        chain: base,
        transport: http(`https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`),
      });

      // Read USDC balance
      const balanceRaw = await publicClient.readContract({
        address: USDC_ADDRESS,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [walletAddress as Address],
      });

      // Format balance (USDC has 6 decimals)
      const formattedBalance = parseFloat(formatUnits(balanceRaw, 6));
      setBalance(formattedBalance);
    } catch (err: any) {
      console.error('Error fetching USDC balance:', err);
      setError(err.message || 'Failed to fetch balance');
      setBalance(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [privyUser?.wallet?.address]);

  return { balance, isLoading, error, refetch: fetchBalance };
}
